import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDownAZ, ArrowLeft, ArrowUpAZ, ChevronDown, ChevronUp, Copy, FileSpreadsheet, Folder, Landmark, Pencil, Plus, Save, Search, ShieldAlert, Trash2, Upload, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = [
  "Corporate Golf Outing",
  "Nonprofit / Charity Golf Outing",
  "Private Golf Outing",
  "Memorial / Benefit Golf Outing",
  "Fundraising Golf Outing",
  "Other",
];
const BENEFIT_CATEGORIES = [
  "Advertising / Marketing", "Signage", "Digital Promotion", "Social Media Promotion",
  "Event Admission", "Golfer Entries", "Merchandise", "Food/Beverage",
  "Sponsor Activation", "Event Experience", "Physical Promotional Materials", "Other",
];
const EXPENSE_CATEGORIES = [
  "Marketing", "Printing", "Banners/Signage", "Sponsor Activation", "Experiences",
  "Merchandise", "Food/Beverage", "Promotional Materials", "Equipment/Rentals", "Other",
];
const CUSTOMER_TYPES = ["For-Profit", "501(c)(3)", "Other Nonprofit", "Government/Public Entity", "Association", "Other"];
const EXEMPTION_STATUSES = ["Not Exempt", "Pending Verification", "Verified"];
const EVENT_TAX_STATUSES = ["Taxable", "Non-Taxable", "Exemption Claimed", "Review Required"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const NEXUS_STATUSES = ["None", "Potential Nexus — Review Required", "Economic Nexus", "Physical Nexus", "Registered"];

const usd = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format((cents || 0) / 100);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const mono = "font-mono text-[10px] uppercase tracking-[0.15em]";
const btn = "border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent disabled:opacity-50";
const iconBtn = "grid h-8 w-8 place-items-center border border-white/15 text-white/60 transition-colors duration-200 hover:border-brand-accent hover:text-brand-accent";

/* Worksheet cell styles (light Excel look) */
const cellInput = "w-full bg-transparent px-3 py-2.5 text-sm text-neutral-800 outline-none focus:bg-fairway-gold/10";
const cellNum = `${cellInput} text-right tabular-nums`;
const thCls = "border border-neutral-300 bg-fairway px-3 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-fairway-cream";
const tdCls = "border border-neutral-300 bg-white";
const totalCls = "border border-neutral-300 bg-fairway-cream px-3 py-2.5 text-right font-mono text-sm font-bold tabular-nums text-fairway-deep";

const uuid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const toCents = (v) => Math.round((parseFloat(v) || 0) * 100);
const newRevLine = () => ({ key: uuid(), source: "", sponsorship_type: "", amount: "", sponsor_address: "", sponsor_city: "", sponsor_state: "", sponsor_zip: "", benefits: [], open: false });
const newExpLine = () => ({ key: uuid(), vendor: "", category: "", expense_category: "Other", description: "", pre_tax: "", sales_tax_paid: "", other_fees: "" });
const newOtherCharge = () => ({ key: uuid(), description: "", amount: "", taxable: true });
const wsInput = `${cellInput} border border-neutral-300 bg-white`;
const wsLabel = "mb-1 block font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-neutral-500";

/* ---------------- Tax status display ---------------- */
const TAX_STATUS_META = {
  taxable: { label: "Taxable", cls: "bg-green-100 text-green-800 border-green-300" },
  non_taxable: { label: "Non-Taxable", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" },
  partially_taxable: { label: "Partially Taxable", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  exempt: { label: "Exempt", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  review_required: { label: "Tax Review Required", cls: "bg-red-100 text-red-700 border-red-300" },
  not_configured: { label: "Tax Integration Not Configured", cls: "bg-neutral-200 text-neutral-600 border-neutral-400" },
};

const TaxBadge = ({ status, testid }) => {
  const meta = TAX_STATUS_META[status] || TAX_STATUS_META.review_required;
  return (
    <span className={`inline-block border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] ${meta.cls}`} data-testid={testid}>
      {meta.label}
    </span>
  );
};

const TaxBlock = ({ tax, baseCents, testid }) => {
  if (!tax) {
    return (
      <div className="border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400" data-testid={testid}>
        Tax determined when the event is saved
      </div>
    );
  }
  return (
    <div className="border border-neutral-300 bg-neutral-50 px-3 py-2.5" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-600">
        <span>Base: <b className="text-neutral-900">{usd(baseCents)}</b></span>
        <span>Tax Status: <TaxBadge status={tax.status} testid={`${testid}-status`} /></span>
        {tax.status !== "not_configured" && tax.status !== "review_required" && (
          <>
            <span>Taxable: <b className="text-neutral-900">{usd(tax.taxable_cents)}</b></span>
            <span>Rate: <b className="text-neutral-900">{(tax.rate * 100).toFixed(3)}%</b></span>
            <span>Sales Tax: <b className="text-neutral-900">{usd(tax.tax_cents)}</b></span>
            <span>Total Payment: <b className="text-fairway">{usd(baseCents + tax.tax_cents)}</b></span>
          </>
        )}
      </div>
      {tax.message && <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-red-600">{tax.message}</p>}
      {tax.jurisdictions?.length > 0 && (
        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-neutral-400">Jurisdictions: {tax.jurisdictions.join(", ")}</p>
      )}
    </div>
  );
};

/* ---------------- Revenue line-item table (module scope — focus safety) ---------------- */
const RevenueTable = ({ lines, setLines, savedLines }) => {
  const setLine = (key, field) => (e) => setLines(lines.map((l) => (l.key === key ? { ...l, [field]: e.target.value } : l)));
  const toggleBenefit = (key, benefit) =>
    setLines(lines.map((l) => l.key === key ? { ...l, benefits: l.benefits.includes(benefit) ? l.benefits.filter((b) => b !== benefit) : [...l.benefits, benefit] } : l));
  const toggleOpen = (key) => setLines(lines.map((l) => (l.key === key ? { ...l, open: !l.open } : l)));
  const total = lines.reduce((s, l) => s + toCents(l.amount), 0);
  return (
    <div className="mt-8">
      <div className="border border-neutral-300 bg-fairway-gold px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-fairway-deep">Sponsorship Revenue</div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse" data-testid="ws-revenue-table">
          <thead>
            <tr>
              <th className={thCls}>Sponsor / Revenue Source</th>
              <th className={thCls}>Sponsorship Type</th>
              <th className={`${thCls} w-36`}>Amount</th>
              <th className={`${thCls} w-24`}>Details &amp; Tax</th>
              <th className={`${thCls} w-10`} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const savedTax = savedLines?.[i]?.tax || null;
              return (
                <FragmentRow
                  key={l.key}
                  row={
                    <tr data-testid={`rev-line-${i}`}>
                      <td className={tdCls}><input value={l.source} onChange={setLine(l.key, "source")} data-testid={`rev-source-${i}`} className={cellInput} placeholder="ABC Company" /></td>
                      <td className={tdCls}><input value={l.sponsorship_type} onChange={setLine(l.key, "sponsorship_type")} data-testid={`rev-type-${i}`} className={cellInput} placeholder="Title Sponsor" /></td>
                      <td className={tdCls}><input type="number" min="0" step="0.01" value={l.amount} onChange={setLine(l.key, "amount")} data-testid={`rev-amount-${i}`} className={cellNum} placeholder="0" /></td>
                      <td className={`${tdCls} text-center`}>
                        <button type="button" onClick={() => toggleOpen(l.key)} data-testid={`rev-details-${i}`} className="inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-fairway hover:text-fairway-gold">
                          {l.open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {l.open ? "Hide" : "Edit"}
                        </button>
                      </td>
                      <td className={`${tdCls} w-10 text-center`}>
                        <button type="button" onClick={() => setLines(lines.filter((x) => x.key !== l.key))} data-testid={`rev-remove-${i}`} className="text-neutral-400 transition-colors hover:text-red-500" aria-label="Remove line">
                          <Trash2 className="mx-auto h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  }
                  details={
                    l.open ? (
                      <tr>
                        <td colSpan={5} className="border border-neutral-300 bg-neutral-50/60 p-4">
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <input value={l.sponsor_address} onChange={setLine(l.key, "sponsor_address")} data-testid={`rev-address-${i}`} className={`${cellInput} border border-neutral-300 bg-white`} placeholder="Sponsor Address" />
                            <input value={l.sponsor_city} onChange={setLine(l.key, "sponsor_city")} data-testid={`rev-city-${i}`} className={`${cellInput} border border-neutral-300 bg-white`} placeholder="City" />
                            <select value={l.sponsor_state} onChange={setLine(l.key, "sponsor_state")} data-testid={`rev-state-${i}`} className={`${cellInput} border border-neutral-300 bg-white`}>
                              <option value="">State</option>
                              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <input value={l.sponsor_zip} onChange={setLine(l.key, "sponsor_zip")} data-testid={`rev-zip-${i}`} className={`${cellInput} border border-neutral-300 bg-white`} placeholder="ZIP" />
                          </div>
                          <p className="mt-3 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-neutral-500">Sponsorship benefits (select all that apply)</p>
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`rev-benefits-${i}`}>
                            {BENEFIT_CATEGORIES.map((b) => (
                              <button
                                key={b}
                                type="button"
                                onClick={() => toggleBenefit(l.key, b)}
                                data-testid={`rev-benefit-${i}-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.1em] transition-colors ${l.benefits.includes(b) ? "border-fairway bg-fairway text-fairway-cream" : "border-neutral-300 bg-white text-neutral-500 hover:border-fairway"}`}
                              >
                                {b}
                              </button>
                            ))}
                          </div>
                          <div className="mt-3">
                            <TaxBlock tax={savedTax} baseCents={toCents(l.amount)} testid={`rev-tax-${i}`} />
                          </div>
                        </td>
                      </tr>
                    ) : null
                  }
                />
              );
            })}
            <tr>
              <td colSpan={5} className="border border-neutral-300 bg-neutral-50 px-3 py-2">
                <button type="button" onClick={() => setLines([...lines, newRevLine()])} data-testid="ws-add-revenue" className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-fairway hover:text-fairway-gold">
                  <Plus className="h-3.5 w-3.5" /> Add Sponsorship Line
                </button>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="border border-neutral-300 bg-fairway-cream px-3 py-2.5 text-right font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-fairway-deep">
                Total Sponsorship Revenue <span className="normal-case text-neutral-400">(base — sales tax never included)</span>
              </td>
              <td className={totalCls} data-testid="ws-total-rev">{usd(total)}</td>
              <td colSpan={2} className="border border-neutral-300 bg-fairway-cream" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FragmentRow = ({ row, details }) => (<>{row}{details}</>);

/* ---------------- Expense line-item table ---------------- */
const ExpenseTable = ({ lines, setLines }) => {
  const setLine = (key, field) => (e) => setLines(lines.map((l) => (l.key === key ? { ...l, [field]: e.target.value } : l)));
  const lineTotal = (l) => toCents(l.pre_tax) + toCents(l.sales_tax_paid) + toCents(l.other_fees);
  const total = lines.reduce((s, l) => s + lineTotal(l), 0);
  return (
    <div className="mt-8">
      <div className="border border-neutral-300 bg-fairway-gold px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-fairway-deep">Expenses</div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse" data-testid="ws-expense-table">
          <thead>
            <tr>
              <th className={thCls}>Vendor</th>
              <th className={thCls}>Expense</th>
              <th className={thCls}>Category</th>
              <th className={thCls}>Description</th>
              <th className={`${thCls} w-28`}>Pre-Tax</th>
              <th className={`${thCls} w-28`}>Sales Tax Paid</th>
              <th className={`${thCls} w-24`}>Other Tax/Fees</th>
              <th className={`${thCls} w-28`}>Total</th>
              <th className={`${thCls} w-10`} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.key} data-testid={`exp-line-${i}`}>
                <td className={tdCls}><input value={l.vendor} onChange={setLine(l.key, "vendor")} data-testid={`exp-vendor-${i}`} className={cellInput} placeholder="PrintCo" /></td>
                <td className={tdCls}><input value={l.category} onChange={setLine(l.key, "category")} data-testid={`exp-category-${i}`} className={cellInput} placeholder="Banners" /></td>
                <td className={tdCls}>
                  <select value={l.expense_category} onChange={setLine(l.key, "expense_category")} data-testid={`exp-cat-${i}`} className={`${cellInput} appearance-none`}>
                    {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className={tdCls}><input value={l.description} onChange={setLine(l.key, "description")} data-testid={`exp-description-${i}`} className={cellInput} placeholder="Sponsor/event signage" /></td>
                <td className={tdCls}><input type="number" min="0" step="0.01" value={l.pre_tax} onChange={setLine(l.key, "pre_tax")} data-testid={`exp-amount-${i}`} className={cellNum} placeholder="0" /></td>
                <td className={tdCls}><input type="number" min="0" step="0.01" value={l.sales_tax_paid} onChange={setLine(l.key, "sales_tax_paid")} data-testid={`exp-tax-${i}`} className={cellNum} placeholder="0" /></td>
                <td className={tdCls}><input type="number" min="0" step="0.01" value={l.other_fees} onChange={setLine(l.key, "other_fees")} data-testid={`exp-other-${i}`} className={cellNum} placeholder="0" /></td>
                <td className={`${tdCls} bg-neutral-50 px-3 text-right font-mono text-xs font-bold tabular-nums text-neutral-800`} data-testid={`exp-total-${i}`}>{usd(lineTotal(l))}</td>
                <td className={`${tdCls} w-10 text-center`}>
                  <button type="button" onClick={() => setLines(lines.filter((x) => x.key !== l.key))} data-testid={`exp-remove-${i}`} className="text-neutral-400 transition-colors hover:text-red-500" aria-label="Remove line">
                    <Trash2 className="mx-auto h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={9} className="border border-neutral-300 bg-neutral-50 px-3 py-2">
                <button type="button" onClick={() => setLines([...lines, newExpLine()])} data-testid="ws-add-expense" className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-fairway hover:text-fairway-gold">
                  <Plus className="h-3.5 w-3.5" /> Add Expense Line
                </button>
              </td>
            </tr>
            <tr>
              <td colSpan={7} className="border border-neutral-300 bg-fairway-cream px-3 py-2.5 text-right font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-fairway-deep">Total Expenses</td>
              <td className={totalCls} data-testid="ws-total-exp">{usd(total)}</td>
              <td className="border border-neutral-300 bg-fairway-cream" />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------- Tax information section (module scope — focus safety) ---------------- */
const TaxInfoSection = ({ taxInfo, setTaxInfo, exempt, setExempt, customer, headers }) => {
  const setT = (k) => (e) => setTaxInfo((f) => ({ ...f, [k]: e.target.value }));
  const setTSel = (k) => (e) => setTaxInfo((f) => ({ ...f, [k]: e.target.value === "yes" }));
  const setE = (k) => (e) => setExempt((f) => ({ ...f, [k]: e.target.value }));
  const setCharge = (key, field) => (e) =>
    setTaxInfo((f) => ({
      ...f,
      other_charges: f.other_charges.map((c) => (c.key === key ? { ...c, [field]: field === "taxable" ? e.target.checked : e.target.value } : c)),
    }));
  const otherTotal = taxInfo.other_charges.reduce((s, c) => s + toCents(c.amount), 0);
  const exemptVerified = exempt.exemption_status === "Verified" && exempt.cert_on_file;
  const exemptState = exemptVerified
    ? { label: "EXEMPT — VERIFIED (certificate on file; provider still validates per transaction)", cls: "border-blue-300 bg-blue-100 text-blue-800" }
    : exempt.exemption_status === "Pending Verification"
      ? { label: "EXEMPTION PENDING — DOCUMENTATION REVIEW REQUIRED", cls: "border-amber-300 bg-amber-100 text-amber-800" }
      : { label: "NOT EXEMPT — STANDARD TAX RULES APPLY", cls: "border-neutral-300 bg-neutral-100 text-neutral-600" };

  const uploadCert = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/news/admin/media`, fd, { headers });
      setExempt((f) => ({ ...f, cert_media_id: res.data.media.id, cert_on_file: true }));
      toast.success("Exemption certificate stored in this customer file.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
  };

  return (
    <div className="mt-8" data-testid="ws-tax-section">
      <div className="border border-neutral-300 bg-fairway-gold px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-fairway-deep">Tax Information</div>
      <div className="border border-t-0 border-neutral-300 bg-white p-4 sm:p-5">
        <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.12em] text-neutral-500">
          Sales tax is never applied blindly to all money flowing through an event. Fairway/FORE service or booking fees, sponsorship revenue, reimbursed/event expenses, and other charges are each treated separately below.
        </p>

        {/* Tax treatment of each money category */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className={wsLabel}>Event Tax Status</p>
            <select value={taxInfo.event_tax_status} onChange={setT("event_tax_status")} data-testid="ws-tax-status" className={`${wsInput} appearance-none`}>
              {EVENT_TAX_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <p className={wsLabel}>Service / Booking Fee</p>
            <select value={taxInfo.service_fee_taxable ? "yes" : "no"} onChange={setTSel("service_fee_taxable")} data-testid="ws-tax-service-taxable" className={`${wsInput} appearance-none`}>
              <option value="yes">Taxable</option>
              <option value="no">Non-Taxable</option>
            </select>
          </div>
          <div>
            <p className={wsLabel}>Sponsorship Revenue</p>
            <select value={taxInfo.sponsorships_taxable ? "yes" : "no"} onChange={setTSel("sponsorships_taxable")} data-testid="ws-tax-sponsors-taxable" className={`${wsInput} appearance-none`}>
              <option value="yes">Taxable</option>
              <option value="no">Non-Taxable</option>
            </select>
          </div>
          <div>
            <p className={wsLabel}>Event Expenses</p>
            <select value={taxInfo.expenses_reimbursed ? "yes" : "no"} onChange={setTSel("expenses_reimbursed")} data-testid="ws-tax-expenses-reimbursed" className={`${wsInput} appearance-none`}>
              <option value="no">Paid by Fairway (not reimbursed)</option>
              <option value="yes">Reimbursed by Customer</option>
            </select>
          </div>
        </div>

        {/* Other charges */}
        <p className={`${wsLabel} mt-5`}>Other Taxable / Non-Taxable Charges</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse" data-testid="ws-tax-other-table">
            <thead>
              <tr>
                <th className={thCls}>Description</th>
                <th className={`${thCls} w-32`}>Amount</th>
                <th className={`${thCls} w-24 text-center`}>Taxable?</th>
                <th className={`${thCls} w-10`} />
              </tr>
            </thead>
            <tbody>
              {taxInfo.other_charges.map((c, i) => (
                <tr key={c.key} data-testid={`ws-tax-other-${i}`}>
                  <td className={tdCls}><input value={c.description} onChange={setCharge(c.key, "description")} data-testid={`ws-tax-other-desc-${i}`} className={cellInput} placeholder="e.g. Equipment rental passed through" /></td>
                  <td className={tdCls}><input type="number" min="0" step="0.01" value={c.amount} onChange={setCharge(c.key, "amount")} data-testid={`ws-tax-other-amount-${i}`} className={cellNum} placeholder="0" /></td>
                  <td className={`${tdCls} text-center`}>
                    <input type="checkbox" checked={c.taxable} onChange={setCharge(c.key, "taxable")} data-testid={`ws-tax-other-taxable-${i}`} className="h-4 w-4 accent-[#0F5132]" />
                  </td>
                  <td className={`${tdCls} w-10 text-center`}>
                    <button type="button" onClick={() => setTaxInfo((f) => ({ ...f, other_charges: f.other_charges.filter((x) => x.key !== c.key) }))} data-testid={`ws-tax-other-remove-${i}`} className="text-neutral-400 transition-colors hover:text-red-500" aria-label="Remove charge">
                      <Trash2 className="mx-auto h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={4} className="border border-neutral-300 bg-neutral-50 px-3 py-2">
                  <button type="button" onClick={() => setTaxInfo((f) => ({ ...f, other_charges: [...f.other_charges, newOtherCharge()] }))} data-testid="ws-tax-other-add" className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-fairway hover:text-fairway-gold">
                    <Plus className="h-3.5 w-3.5" /> Add Other Charge
                  </button>
                </td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-fairway-cream px-3 py-2 text-right font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-fairway-deep">Total Other Charges</td>
                <td className={totalCls} data-testid="ws-tax-other-total">{usd(otherTotal)}</td>
                <td colSpan={2} className="border border-neutral-300 bg-fairway-cream" />
              </tr>
            </tbody>
          </table>
        </div>

        {/* Exemption claim + documentation */}
        <div className="mt-5 border border-neutral-300 bg-neutral-50 p-4" data-testid="ws-tax-exemption">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-700">
              <input type="checkbox" checked={taxInfo.exemption_claimed} onChange={(e) => setTaxInfo((f) => ({ ...f, exemption_claimed: e.target.checked }))} data-testid="ws-tax-exemption-claimed" className="h-4 w-4 accent-[#0F5132]" />
              Customer claims tax-exempt status for this event
            </label>
            <span className={`border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] ${exemptState.cls}`} data-testid="ws-tax-exemption-state">{exemptState.label}</span>
          </div>
          <p className="mt-2 font-mono text-[9px] uppercase leading-relaxed tracking-[0.12em] text-red-600">
            A customer identifying as a nonprofit or 501(c)(3) is NOT automatically tax-exempt. Exemption applies only when status is Verified AND a valid exemption certificate is on file — and the connected tax provider still validates it per transaction and jurisdiction.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className={wsLabel}>Customer Legal Name</p>
              <input value={exempt.legal_name} onChange={setE("legal_name")} data-testid="ws-tax-legal-name" className={wsInput} placeholder="Legal entity name" />
            </div>
            <div>
              <p className={wsLabel}>Customer Type</p>
              <select value={exempt.customer_type} onChange={setE("customer_type")} data-testid="ws-tax-customer-type" className={`${wsInput} appearance-none`}>
                {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <p className={wsLabel}>Tax Exemption Status</p>
              <select value={exempt.exemption_status} onChange={setE("exemption_status")} data-testid="ws-tax-exemption-status" className={`${wsInput} appearance-none`}>
                {EXEMPTION_STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <p className={wsLabel}>Certificate Type</p>
              <input value={exempt.cert_type} onChange={setE("cert_type")} data-testid="ws-tax-cert-type" className={wsInput} placeholder="e.g. State exempt org certificate" />
            </div>
            <div>
              <p className={wsLabel}>Certificate Number</p>
              <input value={exempt.cert_number} onChange={setE("cert_number")} data-testid="ws-tax-cert-number" className={wsInput} placeholder="Certificate / exemption number" />
            </div>
            <div>
              <p className={wsLabel}>Issuing State</p>
              <select value={exempt.cert_state} onChange={setE("cert_state")} data-testid="ws-tax-cert-state" className={`${wsInput} appearance-none`}>
                <option value="">—</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <p className={wsLabel}>Effective Date</p>
              <input type="date" value={exempt.cert_effective} onChange={setE("cert_effective")} data-testid="ws-tax-cert-effective" className={wsInput} />
            </div>
            <div>
              <p className={wsLabel}>Expiration Date (if applicable)</p>
              <input type="date" value={exempt.cert_expiration} onChange={setE("cert_expiration")} data-testid="ws-tax-cert-expiration" className={wsInput} />
            </div>
            <div>
              <p className={wsLabel}>Certificate Document</p>
              {exempt.cert_media_id ? (
                <div className="flex items-center gap-2">
                  <a href={`/api/media/${exempt.cert_media_id}`} target="_blank" rel="noopener noreferrer" className="border border-fairway px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-fairway hover:bg-fairway hover:text-fairway-cream" data-testid="ws-tax-cert-view">View Certificate</a>
                  <label className="cursor-pointer border border-neutral-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 hover:border-fairway hover:text-fairway" data-testid="ws-tax-cert-replace">
                    Replace
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="ws-tax-cert-file" onChange={(e) => { uploadCert(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>
              ) : (
                <label className="flex w-fit cursor-pointer items-center gap-2 border border-fairway px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-fairway hover:bg-fairway hover:text-fairway-cream" data-testid="ws-tax-cert-upload">
                  <Upload className="h-3.5 w-3.5" /> Upload Certificate (PDF or image)
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="ws-tax-cert-file" onChange={(e) => { uploadCert(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>
          </div>
          <label className="mt-4 flex items-center gap-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-700">
            <input type="checkbox" checked={exempt.cert_on_file} onChange={(e) => setExempt((f) => ({ ...f, cert_on_file: e.target.checked }))} data-testid="ws-tax-cert-on-file" className="h-4 w-4 accent-[#0F5132]" />
            Exemption certificate on file for {customer.name}
          </label>
        </div>

        <div className="mt-4">
          <p className={wsLabel}>Tax Notes</p>
          <input value={taxInfo.notes} onChange={setT("notes")} data-testid="ws-tax-notes" className={wsInput} placeholder="Internal notes on tax treatment for this event" />
        </div>
      </div>
    </div>
  );
};

/* ---------------- Worksheet ---------------- */
const Worksheet = ({ customer, initial, headers, onClose, onSaved }) => {
  const isNew = !initial?.id;
  const [info, setInfo] = useState(
    initial
      ? {
          event_name: initial.event_name, event_date: initial.event_date, event_type: initial.event_type, golfers: String(initial.golfers || ""),
          event_address: initial.event_address || "", event_city: initial.event_city || "", event_state: initial.event_state || "", event_zip: initial.event_zip || "",
          service_fee: String((initial.service_fee_cents ?? 150000) / 100),
        }
      : { event_name: "", event_date: "", event_type: "Corporate Golf Outing", golfers: "", event_address: "", event_city: "", event_state: "", event_zip: "", service_fee: "1500" }
  );
  const [revLines, setRevLines] = useState(
    initial?.revenue_lines?.length
      ? initial.revenue_lines.map((l) => ({ key: l.id || uuid(), source: l.source, sponsorship_type: l.sponsorship_type, amount: String(l.amount_cents / 100 || ""), sponsor_address: l.sponsor_address || "", sponsor_city: l.sponsor_city || "", sponsor_state: l.sponsor_state || "", sponsor_zip: l.sponsor_zip || "", benefits: l.benefits || [], open: false }))
      : [newRevLine()]
  );
  const [expLines, setExpLines] = useState(
    initial?.expense_lines?.length
      ? initial.expense_lines.map((l) => ({ key: l.id || uuid(), vendor: l.vendor || "", category: l.category, expense_category: l.expense_category || "Other", description: l.description, pre_tax: String((l.pre_tax_cents ?? l.amount_cents) / 100 || ""), sales_tax_paid: String((l.sales_tax_paid_cents || 0) / 100 || ""), other_fees: String((l.other_fees_cents || 0) / 100 || "") }))
      : [newExpLine()]
  );
  const [taxInfo, setTaxInfo] = useState(() => {
    const ti = initial?.tax_info;
    return ti
      ? {
          event_tax_status: ti.event_tax_status || "Taxable",
          service_fee_taxable: ti.service_fee_taxable !== false,
          sponsorships_taxable: ti.sponsorships_taxable !== false,
          expenses_reimbursed: !!ti.expenses_reimbursed,
          exemption_claimed: !!ti.exemption_claimed,
          notes: ti.notes || "",
          other_charges: (ti.other_charges || []).map((c) => ({ key: c.id || uuid(), description: c.description, amount: String(c.amount_cents / 100 || ""), taxable: c.taxable !== false })),
        }
      : { event_tax_status: "Taxable", service_fee_taxable: true, sponsorships_taxable: true, expenses_reimbursed: false, exemption_claimed: false, notes: "", other_charges: [] };
  });
  const [exempt, setExempt] = useState({
    legal_name: customer.legal_name || "",
    customer_type: customer.customer_type || "For-Profit",
    exemption_status: customer.exemption_status || "Not Exempt",
    cert_on_file: !!customer.cert_on_file,
    cert_type: customer.cert_type || "",
    cert_number: customer.cert_number || "",
    cert_state: customer.cert_state || "",
    cert_effective: customer.cert_effective || "",
    cert_expiration: customer.cert_expiration || "",
    cert_media_id: customer.cert_media_id || "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(initial || null); // last saved event — source of tax determinations

  const revTotal = revLines.reduce((s, l) => s + toCents(l.amount), 0);
  const expTotal = expLines.reduce((s, l) => s + toCents(l.pre_tax) + toCents(l.sales_tax_paid) + toCents(l.other_fees), 0);
  const net = revTotal - expTotal;
  const fee = net > 0 ? Math.round(net * 0.15) : 0;
  const takeHome = net - fee;
  const serviceFeeCents = toCents(info.service_fee);
  const taxSummary = saved?.tax_summary || null;
  const otherChargesTotal = taxInfo.other_charges.reduce((s, c) => s + toCents(c.amount), 0);
  const declaredTaxable =
    (taxInfo.service_fee_taxable ? serviceFeeCents : 0) +
    (taxInfo.sponsorships_taxable ? revTotal : 0) +
    taxInfo.other_charges.reduce((s, c) => s + (c.taxable ? toCents(c.amount) : 0), 0);
  const taxableCharges = taxSummary ? taxSummary.taxable_charges_cents : declaredTaxable;
  const salesTaxCollected = taxSummary?.total_collected_cents || 0;
  const totalDue = revTotal + serviceFeeCents + otherChargesTotal + salesTaxCollected;

  const setInfo_ = (k) => (e) => setInfo((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!info.event_name.trim()) {
      toast.error("Event name is required.");
      return;
    }
    setBusy(true);
    try {
      // 1) Persist the customer's exemption documentation to the customer record
      await axios.patch(`${API}/admin/fairway-events/customers/${customer.id}/tax`, exempt, { headers });
      // 2) Persist the event, including its TAX INFORMATION section
      const payload = {
        ...info,
        golfers: parseInt(info.golfers, 10) || 0,
        service_fee: info.service_fee || 0,
        revenue_lines: revLines.map((l) => ({ source: l.source, sponsorship_type: l.sponsorship_type, amount: l.amount || 0, sponsor_address: l.sponsor_address, sponsor_city: l.sponsor_city, sponsor_state: l.sponsor_state, sponsor_zip: l.sponsor_zip, benefits: l.benefits })),
        expense_lines: expLines.map((l) => ({ vendor: l.vendor, category: l.category, expense_category: l.expense_category, description: l.description, pre_tax: l.pre_tax || 0, sales_tax_paid: l.sales_tax_paid || 0, other_fees: l.other_fees || 0 })),
        tax_info: {
          event_tax_status: taxInfo.event_tax_status,
          service_fee_taxable: taxInfo.service_fee_taxable,
          sponsorships_taxable: taxInfo.sponsorships_taxable,
          expenses_reimbursed: taxInfo.expenses_reimbursed,
          exemption_claimed: taxInfo.exemption_claimed,
          notes: taxInfo.notes,
          other_charges: taxInfo.other_charges.map((c) => ({ description: c.description, amount: c.amount || 0, taxable: c.taxable })),
        },
      };
      const res = isNew && !saved?.id
        ? await axios.post(`${API}/admin/fairway-events/customers/${customer.id}/events`, payload, { headers })
        : await axios.patch(`${API}/admin/fairway-events/events/${saved?.id || initial.id}`, payload, { headers });
      setSaved(res.data.event);
      toast.success("Event and tax information saved. Tax determinations updated.");
      onSaved(res.data.event, { keepOpen: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save event.");
    } finally {
      setBusy(false);
    }
  };

  const exportXlsx = async () => {
    const id = saved?.id || initial?.id;
    try {
      const res = await axios.get(`${API}/admin/fairway-events/events/${id}/export`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${customer.name}_${info.event_name}_${(info.event_date || "").slice(0, 4) || "Event"}_FairwayPartners.xlsx`.replace(/[^A-Za-z0-9._\-]+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded.");
    } catch {
      toast.error("Could not export report.");
    }
  };

  const infoRows = [
    { label: "Event Name", node: <input value={info.event_name} onChange={setInfo_("event_name")} data-testid="ws-field-name" className={cellInput} placeholder="2026 Annual Golf Outing" /> },
    { label: "Event Date", node: <input type="date" value={info.event_date} onChange={setInfo_("event_date")} data-testid="ws-field-date" className={cellInput} /> },
    {
      label: "Event Type",
      node: (
        <select value={info.event_type} onChange={setInfo_("event_type")} data-testid="ws-field-type" className={`${cellInput} appearance-none`}>
          {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      ),
    },
    { label: "Number of Golfers", node: <input type="number" min="0" value={info.golfers} onChange={setInfo_("golfers")} data-testid="ws-field-golfers" className={cellNum} placeholder="120" /> },
    { label: "Event Address", node: <input value={info.event_address} onChange={setInfo_("event_address")} data-testid="ws-field-address" className={cellInput} placeholder="1 Clubhouse Drive" /> },
    { label: "Event City", node: <input value={info.event_city} onChange={setInfo_("event_city")} data-testid="ws-field-city" className={cellInput} placeholder="Austin" /> },
    {
      label: "Event State",
      node: (
        <select value={info.event_state} onChange={setInfo_("event_state")} data-testid="ws-field-state" className={`${cellInput} appearance-none`}>
          <option value="">Select state</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ),
    },
    { label: "Event ZIP Code", node: <input value={info.event_zip} onChange={setInfo_("event_zip")} data-testid="ws-field-zip" className={cellInput} placeholder="78701" /> },
    { label: "Service / Booking Fee ($)", node: <input type="number" min="0" step="0.01" value={info.service_fee} onChange={setInfo_("service_fee")} data-testid="ws-field-service-fee" className={cellNum} placeholder="1500" /> },
  ];

  return (
    <div className="fixed inset-0 z-[95] overflow-y-auto bg-[#F4F1E8]" data-lenis-prevent data-testid="fw-worksheet">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="bg-fairway px-5 py-5 text-center">
          <p className="font-fairway text-xl font-bold tracking-wide text-fairway-cream sm:text-2xl" data-testid="ws-brand">FAIRWAY PARTNERS GROUP</p>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-fairway-cream/70">Presented by FORE the Hell of It Media</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-x border-b border-neutral-300 bg-white px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Customer File: <span className="font-bold text-fairway-deep" data-testid="ws-customer">{customer.name}</span>
          </p>
          <button onClick={onClose} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-fairway-deep" data-testid="ws-close">
            <X className="h-4 w-4" /> Close
          </button>
        </div>

        {/* Event information */}
        <div className="mt-8">
          <div className="border border-neutral-300 bg-fairway-gold px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-fairway-deep">Event Information</div>
          <table className="w-full border-collapse" data-testid="ws-info">
            <tbody>
              {infoRows.map((row) => (
                <tr key={row.label}>
                  <td className="w-44 border border-neutral-300 bg-neutral-50 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500 sm:w-56">{row.label}</td>
                  <td className={`${tdCls} p-0`}>{row.node}</td>
                </tr>
              ))}
              <tr>
                <td className="border border-neutral-300 bg-neutral-50 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">Service Fee Tax</td>
                <td className={`${tdCls} p-3`} data-testid="ws-service-fee-tax">
                  <TaxBlock tax={saved?.service_fee_tax || null} baseCents={serviceFeeCents} testid="ws-service-fee-tax-block" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <RevenueTable lines={revLines} setLines={setRevLines} savedLines={saved?.revenue_lines} />
        <ExpenseTable lines={expLines} setLines={setExpLines} />
        <TaxInfoSection taxInfo={taxInfo} setTaxInfo={setTaxInfo} exempt={exempt} setExempt={setExempt} customer={customer} headers={headers} />

        {/* Financial summary — existing 85/15 model, untouched math */}
        <div className="mt-8">
          <div className="border border-neutral-300 bg-fairway-gold px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-fairway-deep">Financial Summary</div>
          <table className="w-full border-collapse" data-testid="ws-summary">
            <tbody>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Sponsorship Revenue <span className="font-normal normal-case tracking-normal text-neutral-400">(base — sales tax never included)</span></td>
                <td className="w-44 border border-neutral-300 bg-white px-3 py-2.5 text-right font-mono text-sm font-bold tabular-nums text-neutral-900" data-testid="ws-sum-revenue">{usd(revTotal)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Expenses <span className="font-normal normal-case tracking-normal text-neutral-400">(actual amounts paid)</span></td>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 text-right font-mono text-sm font-bold tabular-nums text-neutral-900" data-testid="ws-sum-expenses">{usd(expTotal)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-700">Net Profit Before Fairway Split</td>
                <td className="w-44 border border-neutral-300 bg-white px-3 py-3 text-right font-mono text-base font-bold tabular-nums text-neutral-900" data-testid="ws-net">{usd(net)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-fairway-cream px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-fairway-deep">
                  <span data-testid="ws-customer-label">{customer.name} – 85%</span>
                  <span className="ml-2 text-[9px] font-normal normal-case tracking-normal text-neutral-500">(host distribution of event proceeds — not a sale, no sales tax applied)</span>
                </td>
                <td className="border border-neutral-300 bg-fairway-cream px-3 py-3 text-right font-mono text-base font-bold tabular-nums text-fairway-deep" data-testid="ws-customer-share">{usd(takeHome)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-fairway px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-fairway-gold">Fairway Partners – 15%</td>
                <td className="border border-neutral-300 bg-fairway px-3 py-3 text-right font-mono text-base font-bold tabular-nums text-fairway-gold" data-testid="ws-fairway-share">{usd(fee)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-700">Fairway Service / Booking Fee</td>
                <td className="border border-neutral-300 bg-white px-3 py-3 text-right font-mono text-base font-bold tabular-nums text-neutral-900" data-testid="ws-service-fee">{usd(serviceFeeCents)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-fairway px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-fairway-gold">Total Fairway Revenue <span className="text-[9px] font-normal normal-case tracking-normal text-fairway-cream/70">(15% + service fee — sales tax never included)</span></td>
                <td className="border border-neutral-300 bg-fairway px-3 py-3 text-right font-mono text-base font-bold tabular-nums text-fairway-gold" data-testid="ws-total-fairway">{usd(fee + serviceFeeCents)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Taxable Charges / Fees <span className="font-normal normal-case tracking-normal text-neutral-400">(base amounts designated taxable)</span></td>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 text-right font-mono text-sm font-bold tabular-nums text-neutral-900" data-testid="ws-taxable-charges">{usd(taxableCharges)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Sales Tax Collected <span className="font-normal normal-case tracking-normal text-neutral-400">(liability — never revenue, profit, or part of the 85/15 split)</span></td>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 text-right font-mono text-sm font-bold tabular-nums text-neutral-900" data-testid="ws-sales-tax-collected">{usd(salesTaxCollected)}</td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-fairway-deep px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-fairway-gold">Total Amount Due / Collected <span className="text-[9px] font-normal normal-case tracking-normal text-fairway-cream/70">(sponsorships + service fee + other charges + sales tax)</span></td>
                <td className="border border-neutral-300 bg-fairway-deep px-3 py-3 text-right font-mono text-base font-bold tabular-nums text-fairway-gold" data-testid="ws-total-due">{usd(totalDue)}</td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 font-mono text-[10px] italic tracking-wide text-neutral-500" data-testid="ws-calc-note">
            Calculations: Net Profit = Sponsorship Revenue − Expenses · Fairway Partners = Net Profit × 15% · Customer = Net Profit × 85%
          </p>
        </div>

        {/* Tax summary — separate from revenue/profit */}
        <div className="mt-8">
          <div className="border border-neutral-300 bg-fairway px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-fairway-gold">Tax Summary — separate from revenue and profit</div>
          {!taxSummary || !taxSummary.configured ? (
            <div className="border border-t-0 border-neutral-300 bg-white px-4 py-4" data-testid="ws-tax-not-configured">
              <TaxBadge status="not_configured" testid="ws-tax-config-badge" />
              <p className="mt-2 text-xs leading-relaxed text-neutral-500">
                Automatic tax determinations are not live. Connect Avalara AvaTax credentials in the backend environment (AVALARA_ACCOUNT_ID, AVALARA_LICENSE_KEY, AVALARA_COMPANY_CODE) to enable them. Until then every transaction is marked Tax Review Required — no rates are guessed.
              </p>
            </div>
          ) : null}
          <table className="w-full border-collapse" data-testid="ws-tax-summary">
            <tbody>
              {[
                ["Sales Tax Collected — Sponsorships", usd(taxSummary?.collected_sponsorship_cents || 0), "ws-tax-collected-sponsorships"],
                ["Sales Tax Collected — Service Fees", usd(taxSummary?.collected_service_fee_cents || 0), "ws-tax-collected-service"],
                ["Sales Tax Collected — Other Charges", usd(taxSummary?.collected_other_cents || 0), "ws-tax-collected-other"],
                ["Total Sales Tax Collected (liability — not revenue)", usd(taxSummary?.total_collected_cents || 0), "ws-tax-total-collected"],
                ["Sales Tax Paid on Event Expenses", usd(taxSummary?.tax_paid_expenses_cents || 0), "ws-tax-paid-expenses"],
              ].map(([label, value, tid]) => (
                <tr key={tid}>
                  <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</td>
                  <td className="w-44 border border-neutral-300 bg-white px-3 py-2.5 text-right font-mono text-sm font-bold tabular-nums text-neutral-900" data-testid={tid}>{value}</td>
                </tr>
              ))}
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Tax Review Items</td>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-red-600" data-testid="ws-tax-review-items">
                  {taxSummary?.review_items?.length ? taxSummary.review_items.join("; ") : "None"}
                </td>
              </tr>
              <tr>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">Tax Jurisdiction(s) / Provider</td>
                <td className="border border-neutral-300 bg-white px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-600" data-testid="ws-tax-jurisdictions">
                  {taxSummary?.jurisdictions?.length ? taxSummary.jurisdictions.join(", ") : "—"} {taxSummary?.provider ? `· ${taxSummary.provider}` : ""}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 font-mono text-[10px] italic tracking-wide text-neutral-500">
            Sales tax collected is a liability held for remittance — it never increases revenue, net profit, or either 85/15 share. Tax determinations are evaluated by the connected provider at save time and stored in the audit trail.
          </p>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap items-center gap-3 pb-10">
          <button onClick={save} disabled={busy} data-testid="ws-save" className="flex items-center gap-2 bg-fairway px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-fairway-cream transition-colors duration-300 hover:bg-fairway-deep disabled:opacity-60">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Event"}
          </button>
          <button
            onClick={exportXlsx}
            disabled={!saved?.id}
            title={!saved?.id ? "Save the event first" : "Export to Excel"}
            data-testid="ws-export"
            className="flex items-center gap-2 bg-fairway-gold px-6 py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-fairway-deep transition-colors duration-300 hover:bg-fairway-cream disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FileSpreadsheet className="h-4 w-4" /> Export to Excel
          </button>
          {!saved?.id && <span className="self-center font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400">Save first to export</span>}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Customer tax profile panel ---------------- */
const CustomerTaxPanel = ({ customer, headers, onSaved }) => {
  const [form, setForm] = useState({
    legal_name: customer.legal_name || "",
    customer_type: customer.customer_type || "For-Profit",
    exemption_status: customer.exemption_status || "Not Exempt",
    cert_on_file: !!customer.cert_on_file,
    cert_type: customer.cert_type || "",
    cert_number: customer.cert_number || "",
    cert_state: customer.cert_state || "",
    cert_effective: customer.cert_effective || "",
    cert_expiration: customer.cert_expiration || "",
    cert_media_id: customer.cert_media_id || "",
  });
  const [busy, setBusy] = useState(false);
  const darkInput = "w-full border border-white/20 bg-black/40 px-3.5 py-2.5 text-sm text-white placeholder:text-muted/40 outline-none focus:border-brand-accent";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const needsDocs = form.customer_type !== "For-Profit" && form.customer_type !== "Other";

  const uploadCert = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/news/admin/media`, fd, { headers });
      setForm((f) => ({ ...f, cert_media_id: res.data.media.id, cert_on_file: true }));
      toast.success("Exemption certificate stored in this customer file.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/admin/fairway-events/customers/${customer.id}/tax`, form, { headers });
      toast.success("Tax profile saved.");
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save tax profile.");
    } finally {
      setBusy(false);
    }
  };

  const exemptionState = form.exemption_status === "Verified" && form.cert_on_file
    ? { label: "EXEMPT — VERIFIED (provider still validates per transaction)", cls: "text-blue-300 border-blue-400/40" }
    : form.exemption_status === "Pending Verification"
      ? { label: "EXEMPTION PENDING — DOCUMENTATION REVIEW", cls: "text-yellow-300 border-yellow-400/40" }
      : needsDocs && form.customer_type !== "For-Profit" && form.exemption_status === "Not Exempt"
        ? { label: "NOT EXEMPT", cls: "text-white/60 border-white/20" }
        : { label: "NOT EXEMPT", cls: "text-white/60 border-white/20" };

  return (
    <form onSubmit={save} className="mt-5 border border-white/15 bg-ink-2 p-5" data-testid="fw-tax-profile">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-accent">Customer Tax Profile</p>
        <span className={`border px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.15em] ${exemptionState.cls}`} data-testid="fw-exemption-state">{exemptionState.label}</span>
      </div>
      <p className={`mt-2 ${mono} text-white/35`}>Selecting a nonprofit or government type never makes transactions tax-free by itself — the connected tax provider decides per transaction and jurisdiction, using the documentation stored here.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Customer Legal Name</p>
          <input value={form.legal_name} onChange={set("legal_name")} data-testid="fw-tax-legal-name" className={darkInput} placeholder="Legal entity name" />
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Customer Type</p>
          <select value={form.customer_type} onChange={set("customer_type")} data-testid="fw-tax-customer-type" className={`${darkInput} appearance-none`}>
            {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Tax Exemption Status</p>
          <select value={form.exemption_status} onChange={set("exemption_status")} data-testid="fw-tax-exemption-status" className={`${darkInput} appearance-none`}>
            {EXEMPTION_STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Certificate Type</p>
          <input value={form.cert_type} onChange={set("cert_type")} data-testid="fw-tax-cert-type" className={darkInput} placeholder="e.g. State exempt org certificate" />
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Certificate Number</p>
          <input value={form.cert_number} onChange={set("cert_number")} data-testid="fw-tax-cert-number" className={darkInput} placeholder="Certificate / exemption number" />
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Issuing State</p>
          <select value={form.cert_state} onChange={set("cert_state")} data-testid="fw-tax-cert-state" className={`${darkInput} appearance-none`}>
            <option value="">—</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Effective Date</p>
          <input type="date" value={form.cert_effective} onChange={set("cert_effective")} data-testid="fw-tax-cert-effective" className={`${darkInput} [color-scheme:dark]`} />
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Expiration Date (if applicable)</p>
          <input type="date" value={form.cert_expiration} onChange={set("cert_expiration")} data-testid="fw-tax-cert-expiration" className={`${darkInput} [color-scheme:dark]`} />
        </div>
        <div>
          <p className={`${mono} mb-1.5 text-white/50`}>Certificate Document</p>
          {form.cert_media_id ? (
            <div className="flex items-center gap-2">
              <a href={`/api/media/${form.cert_media_id}`} target="_blank" rel="noopener noreferrer" className={`${btn} !text-brand-accent`} data-testid="fw-tax-cert-view">View Certificate</a>
              <label className={`${btn} cursor-pointer`} data-testid="fw-tax-cert-replace">
                Replace
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="fw-tax-cert-file" onChange={(e) => { uploadCert(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          ) : (
            <label className={`${btn} flex w-fit cursor-pointer items-center gap-2`} data-testid="fw-tax-cert-upload">
              <Upload className="h-3.5 w-3.5" /> Upload Certificate (PDF or image)
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" data-testid="fw-tax-cert-file" onChange={(e) => { uploadCert(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          )}
        </div>
      </div>
      <label className="mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
        <input type="checkbox" checked={form.cert_on_file} onChange={(e) => setForm((f) => ({ ...f, cert_on_file: e.target.checked }))} data-testid="fw-tax-cert-on-file" className="h-4 w-4 accent-[#CCFF00]" />
        Exemption certificate on file
      </label>
      <button type="submit" disabled={busy} className="mt-5 bg-brand-accent px-5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white disabled:opacity-60" data-testid="fw-tax-save">
        {busy ? "Saving…" : "Save Tax Profile"}
      </button>
    </form>
  );
};

/* ---------------- Tax Compliance view ---------------- */
const blankState = { state: "", registered: false, permit_number: "", registration_effective: "", nexus_status: "None", economic_nexus_status: "None", physical_nexus_status: "None", filing_frequency: "", next_filing_deadline: "", last_return_filed: "", notes: "" };

const TaxCompliance = ({ headers }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null); // editing/creating row
  const darkInput = "w-full border border-white/20 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-muted/40 outline-none focus:border-brand-accent";

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/fairway-events/tax/states`, { headers });
      setData(res.data);
    } catch {
      /* auth handled upstream */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/fairway-events/tax/states`, form, { headers });
      toast.success("State registration saved.");
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    }
  };

  const remove = async (state) => {
    if (!window.confirm(`Remove the ${state} registration record?`)) return;
    await axios.delete(`${API}/admin/fairway-events/tax/states/${state}`, { headers });
    toast.success("Removed.");
    load();
  };

  if (!data) return <p className={`${mono} mt-6 text-white/40`}>Loading…</p>;

  return (
    <div data-testid="fw-tax-compliance">
      <div className={`mt-5 flex flex-wrap items-center gap-3 border p-4 ${data.provider?.configured ? "border-green-500/40 bg-green-500/10" : "border-yellow-500/40 bg-yellow-500/10"}`} data-testid="fw-provider-banner">
        <ShieldAlert className={`h-4 w-4 ${data.provider?.configured ? "text-green-400" : "text-yellow-400"}`} />
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">
          {data.provider?.configured
            ? `Tax provider connected — Avalara AvaTax (${data.provider.environment})`
            : "TAX INTEGRATION NOT CONFIGURED — transactions are marked Tax Review Required until Avalara AvaTax credentials are added to the backend environment"}
        </p>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className={`${mono} text-white/40`}>State registrations &amp; nexus tracking</p>
        <button onClick={() => setForm({ ...blankState })} className="flex items-center gap-2 bg-brand-accent px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white" data-testid="fw-add-state">
          <Plus className="h-3.5 w-3.5" /> Add State
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="mt-4 border border-white/15 bg-ink-2 p-5" data-testid="fw-state-form">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>State *</p>
              <select required value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} data-testid="fw-state-select" className={`${darkInput} appearance-none`}>
                <option value="" disabled>Select</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Nexus Status</p>
              <select value={form.nexus_status} onChange={(e) => setForm((f) => ({ ...f, nexus_status: e.target.value }))} data-testid="fw-state-nexus" className={`${darkInput} appearance-none`}>
                {NEXUS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Economic Nexus</p>
              <select value={form.economic_nexus_status} onChange={(e) => setForm((f) => ({ ...f, economic_nexus_status: e.target.value }))} className={`${darkInput} appearance-none`}>
                {NEXUS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Physical Nexus</p>
              <select value={form.physical_nexus_status} onChange={(e) => setForm((f) => ({ ...f, physical_nexus_status: e.target.value }))} className={`${darkInput} appearance-none`}>
                {NEXUS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Sales Tax Permit #</p>
              <input value={form.permit_number} onChange={(e) => setForm((f) => ({ ...f, permit_number: e.target.value }))} data-testid="fw-state-permit" className={darkInput} />
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Registration Effective</p>
              <input type="date" value={form.registration_effective} onChange={(e) => setForm((f) => ({ ...f, registration_effective: e.target.value }))} data-testid="fw-state-effective" className={`${darkInput} [color-scheme:dark]`} />
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Filing Frequency</p>
              <input value={form.filing_frequency} onChange={(e) => setForm((f) => ({ ...f, filing_frequency: e.target.value }))} data-testid="fw-state-frequency" className={darkInput} placeholder="Monthly / Quarterly / Annual" />
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Next Filing Deadline</p>
              <input type="date" value={form.next_filing_deadline} onChange={(e) => setForm((f) => ({ ...f, next_filing_deadline: e.target.value }))} data-testid="fw-state-deadline" className={`${darkInput} [color-scheme:dark]`} />
            </div>
            <div>
              <p className={`${mono} mb-1.5 text-white/50`}>Last Return Filed</p>
              <input type="date" value={form.last_return_filed} onChange={(e) => setForm((f) => ({ ...f, last_return_filed: e.target.value }))} data-testid="fw-state-last-filed" className={`${darkInput} [color-scheme:dark]`} />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <p className={`${mono} mb-1.5 text-white/50`}>Notes</p>
              <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="fw-state-notes" className={darkInput} placeholder="Registration notes, thresholds watched, etc." />
            </div>
          </div>
          <label className="mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
            <input type="checkbox" checked={form.registered} onChange={(e) => setForm((f) => ({ ...f, registered: e.target.checked }))} data-testid="fw-state-registered" className="h-4 w-4 accent-[#CCFF00]" />
            Fairway registered for sales tax in this state
          </label>
          <div className="mt-4 flex gap-3">
            <button type="submit" className="bg-brand-accent px-5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white" data-testid="fw-state-save">Save State</button>
            <button type="button" onClick={() => setForm(null)} className={btn}>Cancel</button>
          </div>
        </form>
      )}

      <div className="mt-5 flex flex-col gap-2" data-testid="fw-state-list">
        {data.states.map((s) => (
          <div key={s.id} className="border border-white/10 bg-ink-2 p-4" data-testid={`fw-state-${s.state}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Landmark className="h-4 w-4 text-brand-accent" />
                <span className="font-display text-lg font-extrabold tracking-tight text-white">{s.state}</span>
                <span className={`px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] ${s.registered ? "bg-green-500/15 text-green-400" : "bg-white/10 text-white/50"}`}>
                  {s.registered ? "Registered" : "Not Registered"}
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setForm({ ...blankState, ...s })} className={btn} data-testid={`fw-state-edit-${s.state}`}>Edit</button>
                <button onClick={() => remove(s.state)} className={iconBtn} data-testid={`fw-state-delete-${s.state}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            {s.alerts.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`fw-state-alerts-${s.state}`}>
                {s.alerts.map((a) => (
                  <span key={a} className="border border-red-500/40 bg-red-500/10 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-red-400">{a}</span>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-3 font-mono text-[10px] uppercase tracking-[0.12em] text-white/45 sm:grid-cols-4 lg:grid-cols-6">
              <span>Nexus: <b className="text-white/75">{s.nexus_status}</b></span>
              <span>Collected: <b className="text-white/75">{usd(s.tax_collected_cents)}</b></span>
              <span>Due: <b className="text-white/75">{usd(s.tax_due_cents)}</b></span>
              <span>Remitted: <b className="text-white/75">{usd(s.tax_remitted_cents)}</b></span>
              <span>Frequency: <b className="text-white/75">{s.filing_frequency || "—"}</b></span>
              <span>Next Deadline: <b className="text-white/75">{s.next_filing_deadline || "—"}</b></span>
            </div>
            {s.permit_number && <p className={`mt-2 ${mono} text-white/35`}>Permit #{s.permit_number}{s.registration_effective ? ` · effective ${s.registration_effective}` : ""}{s.notes ? ` · ${s.notes}` : ""}</p>}
          </div>
        ))}
        {data.states.length === 0 && (
          <p className={`${mono} border border-dashed border-white/15 py-10 text-center text-white/40`} data-testid="fw-states-empty">
            No states tracked yet — add states as Fairway registers or approaches nexus
          </p>
        )}
      </div>
    </div>
  );
};

/* ---------------- Main: filing cabinet + compliance ---------------- */
export default function FairwayEvents({ token, onAuthFail }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [area, setArea] = useState("files"); // files | compliance
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [view, setView] = useState(null); // { customer, events }
  const [worksheet, setWorksheet] = useState(null); // { initial }
  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, id, label }
  const [newCustomer, setNewCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [showTaxProfile, setShowTaxProfile] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/fairway-events/customers`, { headers });
      setCustomers(res.data.customers);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openCustomer = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/fairway-events/customers/${id}`, { headers });
      setView(res.data);
      setShowTaxProfile(false);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
  };

  const refresh = async () => {
    if (view) await openCustomer(view.customer.id);
    await load();
  };

  const addCustomer = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/fairway-events/customers`, { name: newCustomerName }, { headers });
      toast.success("Customer file created.");
      setNewCustomer(false);
      setNewCustomerName("");
      await load();
      openCustomer(res.data.customer.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create customer.");
    }
  };

  const saveRename = async () => {
    try {
      await axios.patch(`${API}/admin/fairway-events/customers/${renaming.id}`, { name: renaming.name }, { headers });
      toast.success("Customer renamed.");
      setRenaming(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rename.");
    }
  };

  const doDelete = async () => {
    try {
      if (confirmDelete.kind === "event") {
        await axios.delete(`${API}/admin/fairway-events/events/${confirmDelete.id}`, { headers });
        toast.success("Event deleted.");
        setConfirmDelete(null);
        await refresh();
      } else {
        await axios.delete(`${API}/admin/fairway-events/customers/${confirmDelete.id}`, { headers });
        toast.success("Customer file and event history deleted.");
        setConfirmDelete(null);
        setView(null);
        await load();
      }
    } catch {
      toast.error("Could not delete.");
    }
  };

  const exportEvent = async (ev) => {
    try {
      const res = await axios.get(`${API}/admin/fairway-events/events/${ev.id}/export`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ev.customer_name}_${ev.event_name}_${(ev.event_date || "").slice(0, 4) || "Event"}_FairwayPartners.xlsx`.replace(/[^A-Za-z0-9._\-]+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded.");
    } catch {
      toast.error("Could not export report.");
    }
  };

  const shown = useMemo(() => {
    const list = customers.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
    return [...list].sort((a, b) => (sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)));
  }, [customers, search, sortAsc]);

  const areaBtn = (active) =>
    `border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
      active ? "border-brand-accent bg-brand-accent text-ink" : "border-white/15 text-muted hover:border-brand-accent hover:text-brand-accent"
    }`;

  return (
    <div className="mt-8" data-testid="fairway-events-tab">
      <div className="flex flex-wrap gap-2" data-testid="fw-areas">
        <button onClick={() => setArea("files")} className={areaBtn(area === "files")} data-testid="fw-area-files">Customer Files</button>
        <button onClick={() => setArea("compliance")} className={areaBtn(area === "compliance")} data-testid="fw-area-compliance">Tax Compliance</button>
      </div>

      {area === "compliance" ? (
        <TaxCompliance headers={headers} />
      ) : !view ? (
        /* ---------------- Customer filing cabinet ---------------- */
        <>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-muted" data-testid="fw-cabinet-heading">Customer Files</h2>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers" data-testid="fw-customer-search" className="w-52 border border-white/20 bg-black/40 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-muted/40 outline-none focus:border-brand-accent" />
              </div>
              <button onClick={() => setSortAsc((v) => !v)} className={`${btn} flex items-center gap-1.5`} data-testid="fw-sort" title="Sort alphabetically">
                {sortAsc ? <ArrowDownAZ className="h-3.5 w-3.5" /> : <ArrowUpAZ className="h-3.5 w-3.5" />}
                {sortAsc ? "A–Z" : "Z–A"}
              </button>
              <button onClick={() => setNewCustomer(true)} className="flex items-center gap-2 bg-brand-accent px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white" data-testid="fw-new-customer">
                <Plus className="h-3.5 w-3.5" /> New Customer
              </button>
            </div>
          </div>

          {newCustomer && (
            <form onSubmit={addCustomer} className="mt-4 flex flex-wrap items-center gap-3 border border-white/15 bg-ink-2 p-4" data-testid="fw-new-customer-form">
              <input required value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} placeholder="Customer / Organization name" data-testid="fw-new-customer-name" className="w-72 border border-white/20 bg-black/40 px-3.5 py-2.5 text-sm text-white outline-none focus:border-brand-accent" />
              <button type="submit" className="bg-brand-accent px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white" data-testid="fw-new-customer-save">Create</button>
              <button type="button" onClick={() => setNewCustomer(false)} className={btn}>Cancel</button>
            </form>
          )}

          <div className="mt-5 flex flex-col gap-1.5" data-testid="fw-customer-list">
            {shown.map((c) => (
              <div key={c.id} className="group flex items-center gap-3 border border-white/10 bg-ink-2 px-4 py-3.5 transition-colors duration-300 hover:border-brand-accent" data-testid={`fw-customer-${c.id}`}>
                {renaming?.id === c.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); saveRename(); }} className="flex flex-1 items-center gap-2">
                    <Folder className="h-4 w-4 shrink-0 text-brand-accent" />
                    <input autoFocus required value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} data-testid="fw-rename-input" className="w-64 border border-white/20 bg-black/40 px-3 py-1.5 text-sm text-white outline-none focus:border-brand-accent" />
                    <button type="submit" className={btn} data-testid="fw-rename-save">Save</button>
                    <button type="button" onClick={() => setRenaming(null)} className={btn}>Cancel</button>
                  </form>
                ) : (
                  <>
                    <button onClick={() => openCustomer(c.id)} className="flex flex-1 items-center gap-3 text-left" data-testid={`fw-folder-${c.id}`}>
                      <Folder className="h-4 w-4 shrink-0 text-brand-accent" />
                      <span className="font-display text-base font-bold tracking-tight text-white group-hover:text-brand-accent">{c.name}</span>
                    </button>
                    <button onClick={() => setRenaming({ id: c.id, name: c.name })} className={iconBtn} data-testid={`fw-rename-${c.id}`} title="Edit customer name"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setConfirmDelete({ kind: "customer", id: c.id, label: c.name })} className={`${iconBtn} hover:!border-red-500/50 hover:!text-red-400`} data-testid={`fw-delete-customer-${c.id}`} title="Delete customer"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            ))}
            {shown.length === 0 && (
              <div className="grid place-items-center gap-3 border border-dashed border-white/15 py-14 text-center" data-testid="fw-customers-empty">
                <Folder className="h-7 w-7 text-white/20" />
                <p className={`${mono} text-white/40`}>{customers.length === 0 ? "No customer files yet — create the first one" : "No customers match your search"}</p>
              </div>
            )}
          </div>
        </>
      ) : (
        /* ---------------- Customer file ---------------- */
        <>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => { setView(null); load(); }} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors hover:text-brand-accent" data-testid="fw-back">
              <ArrowLeft className="h-3.5 w-3.5" /> Customer Files
            </button>
            <div className="flex gap-2">
              <button onClick={() => setShowTaxProfile((v) => !v)} className={`${btn} flex items-center gap-1.5`} data-testid="fw-tax-profile-toggle">
                <ShieldAlert className="h-3.5 w-3.5" /> Tax Profile
              </button>
              <button onClick={() => setWorksheet({ initial: null })} className="flex items-center gap-2 bg-brand-accent px-4 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink hover:bg-white" data-testid="fw-new-event">
                <Plus className="h-3.5 w-3.5" /> Create New Event
              </button>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <Folder className="h-5 w-5 text-brand-accent" />
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight" data-testid="fw-customer-name">{view.customer.name}</h2>
          </div>

          {showTaxProfile && (
            <CustomerTaxPanel
              customer={view.customer}
              headers={headers}
              onSaved={(updated) => setView((v) => ({ ...v, customer: updated }))}
            />
          )}

          <div className="mt-5 flex flex-col gap-1.5" data-testid="fw-event-list">
            {view.events.map((ev) => (
              <div key={ev.id} className="group flex items-center gap-3 border border-white/10 bg-ink-2 px-4 py-3.5 transition-colors duration-300 hover:border-brand-accent" data-testid={`fw-event-${ev.id}`}>
                <button onClick={() => setWorksheet({ initial: ev })} className="flex flex-1 flex-wrap items-baseline gap-x-3 text-left" data-testid={`fw-open-event-${ev.id}`}>
                  <span className="font-display text-base font-bold tracking-tight text-white group-hover:text-brand-accent">{ev.event_name}</span>
                  <span className={`${mono} text-white/35`}>{fmtDate(ev.event_date)} · {ev.event_type} · {ev.golfers} golfers{ev.event_state ? ` · ${ev.event_state}` : ""}</span>
                </button>
                <button
                  onClick={() => setWorksheet({ initial: { ...ev, id: undefined, event_name: `${ev.event_name} (Copy)`, event_date: "" } })}
                  className={iconBtn}
                  data-testid={`fw-event-duplicate-${ev.id}`}
                  title="Duplicate as starting point"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => exportEvent(ev)} className={`${iconBtn} !text-brand-accent`} data-testid={`fw-event-export-${ev.id}`} title="Export to Excel"><FileSpreadsheet className="h-3.5 w-3.5" /></button>
                <button onClick={() => setConfirmDelete({ kind: "event", id: ev.id, label: ev.event_name })} className={`${iconBtn} hover:!border-red-500/50 hover:!text-red-400`} data-testid={`fw-event-delete-${ev.id}`} title="Delete event"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {view.events.length === 0 && (
              <p className={`${mono} border border-dashed border-white/15 py-10 text-center text-white/40`} data-testid="fw-events-empty">
                No events on file — create this customer&apos;s first event
              </p>
            )}
          </div>
        </>
      )}

      {/* ---------------- Worksheet ---------------- */}
      {worksheet && view && (
        <Worksheet
          customer={view.customer}
          initial={worksheet.initial}
          headers={headers}
          onClose={() => setWorksheet(null)}
          onSaved={() => { refresh(); }}
        />
      )}

      {/* ---------------- Delete confirmation ---------------- */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[96] grid place-items-center bg-black/70 p-5 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} data-testid="fw-delete-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border border-red-500/40 bg-ink-2 p-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-red-400">Confirm Delete</p>
            <p className="mt-3 text-sm leading-relaxed text-white/80">
              Permanently delete {confirmDelete.kind === "customer" ? `customer file "${confirmDelete.label}" and their entire event history` : `event "${confirmDelete.label}"`}? This cannot be undone.
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={doDelete} className="bg-red-500 px-5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white hover:bg-red-400" data-testid="fw-delete-confirm">Yes, Delete</button>
              <button onClick={() => setConfirmDelete(null)} className={btn} data-testid="fw-delete-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
